import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import type { NotificationPreference, NotificationProduct } from "../../types";
import { Panel } from "../ui/Panel";
import { PushSettings } from "./PushSettings";

/**
 * Every notification switch, in one place.
 *
 * Rendered from the server's catalogue rather than from a list written here, so
 * adding a notification is one entry in `packages/db/src/notifications.ts` and
 * nothing else. That is the whole reason the endpoint returns labels and
 * descriptions instead of just keys.
 *
 * Per-company subscriptions are deliberately **not** here. Following an account
 * is a decision about that account, made on its page next to the thing it
 * refers to; a list of two hundred companies in a settings dialog would be a
 * worse version of the panel that already exists.
 */

/**
 * One tick box in the grid.
 *
 * A bare checkbox rather than the `Toggle` the rest of settings uses. A switch
 * is right for a labelled row you read left to right; in a column of twenty
 * under a heading, a tick box is what the eye scans, and twenty switches in a
 * column read as a control panel rather than as an answer to a question.
 */
function Tick({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <td className="px-2 py-1.5 text-center">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 accent-brand-600 md:size-3.5"
      />
    </td>
  );
}

const SECTIONS: { product: NotificationProduct; title: string; blurb: string }[] =
  [
    {
      product: "helpdesk",
      title: "Helpdesk",
      blurb: "Tickets, mentions and mail.",
    },
    {
      product: "crm",
      title: "CRM",
      blurb: "Accounts you own. Following a single company is set on its page.",
    },
    {
      product: "sales",
      title: "Sales calls",
      blurb: "Calls assigned to you, and calls on your accounts.",
    },
    {
      product: "meetings",
      title: "Meetings",
      blurb: "How far ahead to be reminded, and changes to what is booked.",
    },
  ];

export function NotificationSettings() {
  const queryClient = useQueryClient();

  const preferences = useQuery({
    queryKey: queryKeys.notificationPreferences,
    queryFn: api.notificationPreferences,
    staleTime: 5 * 60_000,
  });

  const setPreference = useMutation({
    mutationFn: ({
      key,
      channel,
      enabled,
    }: {
      key: string;
      channel: "app" | "email";
      enabled: boolean;
    }) => api.setNotificationPreference(key, channel, enabled),
    /*
     * Optimistic, because a switch that waits for a round trip before moving
     * feels broken -- and the server returns the whole set, so the response
     * corrects anything this got wrong.
     */
    onMutate: async ({ key, channel, enabled }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.notificationPreferences,
      });

      const previous = queryClient.getQueryData<{
        preferences: NotificationPreference[];
      }>(queryKeys.notificationPreferences);

      queryClient.setQueryData<{ preferences: NotificationPreference[] }>(
        queryKeys.notificationPreferences,
        (current) =>
          current
            ? {
                preferences: current.preferences.map((entry) =>
                  entry.key === key
                    ? {
                        ...entry,
                        /* Only the column that was ticked. The two channels
                           share a row on the server but are independent
                           settings, and moving both here would show a change
                           the save is not going to make. */
                        ...(channel === "app"
                          ? { enabled }
                          : { email: enabled }),
                        isDefault: false,
                      }
                    : entry,
                ),
              }
            : current,
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.notificationPreferences,
          context.previous,
        );
      }
    },
    onSuccess: (data) =>
      queryClient.setQueryData(queryKeys.notificationPreferences, data),
  });

  const all = preferences.data?.preferences ?? [];

  return (
    <Panel title="Notifications">
      {preferences.isLoading ? (
        <p className="text-xs text-muted">Loading…</p>
      ) : (
        <div className="space-y-5">
          <p className="text-xs text-muted">
            <strong className="font-medium text-content">Pulse</strong> adds it
            to the bell and its count — nothing pops up over your work.{" "}
            <strong className="font-medium text-content">Email</strong> sends it
            to your address as well, and is off everywhere until you ask for it.
          </p>

          {/*
            Push sits above the table rather than as a third column in it.
            Which notifications you want is a question about your account and
            belongs in the grid; whether this particular phone buzzes is a
            question about this device, and the two do not compose into one
            table. Push then follows the Pulse column -- see PushSettings.
          */}
          <PushSettings />

          {SECTIONS.map((section) => {
            const rows = all.filter(
              (entry) => entry.product === section.product,
            );
            if (rows.length === 0) return null;

            return (
              <section key={section.product} className="space-y-2">
                <div>
                  <h4 className="text-xs font-semibold text-content">
                    {section.title}
                  </h4>
                  <p className="text-xs text-muted">{section.blurb}</p>
                </div>

                {/*
                  A table rather than two toggles a row, because the question
                  being answered is a grid one -- "which of these reach me, and
                  how" -- and two columns of tick boxes can be read down as well
                  as across. The header repeats per section so it is still on
                  screen four sections in.

                  Scrolls inside itself on a narrow window rather than pushing
                  the settings dialog sideways.
                */}
                <div className="overflow-x-auto rounded-md border border-line">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
                        <th className="px-3 py-1.5 font-medium">
                          Tell me when…
                        </th>
                        {/* `w-px` on a phone: as narrow as the word over
                            them, so the sentences get the width instead of
                            wrapping beside two columns of air. */}
                        <th className="w-px px-2 py-1.5 text-center font-medium md:w-16">
                          Pulse
                        </th>
                        <th className="w-px px-2 py-1.5 text-center font-medium md:w-16">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((entry) => (
                        <tr
                          key={entry.key}
                          className="odd:bg-surface even:bg-app"
                        >
                          {/* The label alone. Each one is written as the thing
                              that happens rather than as a setting, which is
                              clear enough on its own -- and a second line under
                              every row of a twenty-row grid turned a table you
                              scan into one you have to read. */}
                          <td className="px-3 py-1.5 text-content">
                            {entry.label}
                          </td>

                          <Tick
                            label={`${entry.label} — in Pulse`}
                            checked={entry.enabled}
                            onChange={(enabled) =>
                              setPreference.mutate({
                                key: entry.key,
                                channel: "app",
                                enabled,
                              })
                            }
                          />

                          <Tick
                            label={`${entry.label} — by email`}
                            checked={entry.email}
                            onChange={(enabled) =>
                              setPreference.mutate({
                                key: entry.key,
                                channel: "email",
                                enabled,
                              })
                            }
                          />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
